// @ts-nocheck
import { useState, useEffect, useMemo, useRef } from 'react';
import useCustomForm from 'pages/Forms/context/useCustomForm';
import { Element } from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/type/preview.type';
import { UpdateAnswerInPrefilledElements } from 'pages/Forms/RegisterForm/types/shareForm';

const useCheckboxField = (
  element: Element,
  answer: string,
  handleDirty: () => void,
  updateAnswerInPrefilledElements?: UpdateAnswerInPrefilledElements,
) => {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [selectedChoices, setSelectedChoices] = useState<string>('');
  const [otherText, setOtherText] = useState<string>('');
  const [, { setAnswer, setSelectedLinkListLabel }] = useCustomForm();

  useEffect(() => {
    if (answer) {
      const otherChoice = element.choice?.find((choice) => choice.isOtherField);
      if (otherChoice && answer.includes(otherChoice.id)) {
        setSelectedChoices(answer);
        setOtherText(element?.selectedLinkListLabel || '');
      } else {
        setSelectedChoices(answer);
        setOtherText('');
      }
    } else {
      setSelectedChoices('');
      setOtherText('');
    }
  }, [answer, element.choice, element?.selectedLinkListLabel]);

  const handleSelectionChange = (choiceId: string) => {
    const selectedChoicesList = selectedChoices.split(',').filter(Boolean);
    const otherChoice = element.choice?.find((choice) => choice.isOtherField);
    const isOtherChoice = otherChoice?.id === choiceId;

    const isAlreadySelected = selectedChoicesList.includes(choiceId);

    let updatedChoices: string;
    if (isAlreadySelected) {
      updatedChoices = selectedChoicesList
        .filter((id) => id !== choiceId)
        .join(',');
    } else {
      updatedChoices =
        selectedChoicesList.length > 0
          ? `${selectedChoices},${choiceId}`
          : choiceId;
    }

    setSelectedChoices(updatedChoices);

    if (isOtherChoice) {
      if (!isAlreadySelected) {
        setTimeout(() => textFieldRef.current?.focus(), 0);
      } else {
        setOtherText('');
        setSelectedLinkListLabel(element.id, '');
      }
    }

    setAnswer(element.id, updatedChoices);
    if (updateAnswerInPrefilledElements) {
      updateAnswerInPrefilledElements(
        element?.id,
        updatedChoices,
        element?.type,
        otherText,
      );
    }
    handleDirty();
  };

  const handleOtherTextChange = (value: string) => {
    setOtherText(value);
    setSelectedLinkListLabel(element.id, value);
    if (updateAnswerInPrefilledElements) {
      updateAnswerInPrefilledElements(
        element?.id,
        answer,
        element?.type,
        value,
      );
    }
    handleDirty();
  };

  const isSelected = (choiceId: string) =>
    selectedChoices.split(',').filter(Boolean).includes(choiceId);

  const isOtherSelected = useMemo(
    () =>
      selectedChoices
        .split(',')
        .filter(Boolean)
        .some((choiceId) => {
          const choice = element.choice?.find((c) => c.id === choiceId);
          return choice && choice.isOtherField;
        }),
    [selectedChoices, element.choice],
  );

  const isShowActionMessage = useMemo(() => {
    const isActionSelected = selectedChoices
      .split(',')
      .filter(Boolean)
      .some((choiceId) => {
        const choice = element.choice?.find((c) => c.id === choiceId);
        return choice && choice.action;
      });
    return isActionSelected && element?.actionMessage?.trim();
  }, [selectedChoices, element.choice, element.actionMessage]);

  const otherChoice = element.choice?.find((choice) => choice.isOtherField);

  const clearSelection = () => {
    setSelectedChoices('');
    setAnswer(element.id, '');
    setOtherText('');
    setSelectedLinkListLabel(element.id, '');
    if (updateAnswerInPrefilledElements) {
      updateAnswerInPrefilledElements(element.id, '', element.type, '');
    }
    handleDirty();
  };

  return {
    selectedChoices,
    otherText,
    handleSelectionChange,
    handleOtherTextChange,
    isSelected,
    isShowActionMessage,
    otherChoice,
    isOtherSelected,
    clearSelection,
    textFieldRef,
  };
};

export default useCheckboxField;
